import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Mic, MicOff, X, Loader2, Check, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const C = {
  bg: '#FDFBF7', text: '#1A1A18', muted: '#9A9590', textSec: '#6A6560',
  accent: '#C67A5C', green: '#5A8C6A', gold: '#C6A05C', purple: '#7B6B9E',
};

export function VoiceMicPopup({ open, onClose }) {
  const { authHeaders, token } = useAuth();
  const [phase, setPhase] = useState('idle'); // idle | listening | processing | result | error
  const [transcript, setTranscript] = useState('');
  const [result, setResult] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  // Auto-start listening when popup opens
  useEffect(() => {
    if (open && phase === 'idle') {
      startListening();
    }
    if (!open) {
      stopStream();
      setPhase('idle');
      setTranscript('');
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => processAudio();
      mr.start();
      setPhase('listening');
    } catch {
      setPhase('idle');
      // Mic not available — let user type instead
    }
  };

  const stopListening = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    stopStream();
    setPhase('processing');
  };

  const processAudio = async () => {
    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    if (blob.size < 1000) { setPhase('idle'); return; }
    const formData = new FormData();
    formData.append('file', blob, 'recording.webm');
    try {
      // Transcribe
      const transcribeRes = await axios.post(`${API}/voice-convo/transcribe`, formData, {
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });
      const text = transcribeRes.data?.text?.trim();
      if (!text) { setPhase('idle'); return; }
      setTranscript(text);
      await sendToNuri(text);
    } catch {
      setPhase('error');
    }
  };

  const sendToNuri = async (text) => {
    setPhase('processing');
    try {
      const res = await axios.post(`${API}/voice-convo/chat`, {
        transcript: text, history: [], session_id: `popup_${Date.now()}`,
      }, authHeaders());
      setResult(res.data);
      setPhase('result');
      if (res.data.logged_meal_id) {
        window.dispatchEvent(new Event('nuri-meal-logged'));
      }
    } catch {
      setPhase('error');
    }
  };

  const handleReset = () => {
    setPhase('idle');
    setTranscript('');
    setResult(null);
    setTimeout(() => startListening(), 100);
  };

  if (!open) return null;

  return (
    <div
      data-testid="voice-popup-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 999,
        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: '16px 16px 100px',
      }}
    >
      <div
        data-testid="voice-popup"
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 400, borderRadius: 28,
          background: C.bg, boxShadow: '0 -8px 40px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          animation: 'voice-popup-slide 0.3s ease-out',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 0' }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: C.text, fontFamily: 'var(--font-sans)' }}>
            {phase === 'listening' ? 'Listening...' : phase === 'processing' ? 'Thinking...' : phase === 'result' ? 'Logged!' : 'Talk to Nuri'}
          </p>
          <button data-testid="voice-popup-close" onClick={onClose} style={{
            width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X style={{ width: 16, height: 16, color: C.muted }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 20px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 200 }}>

          {/* Idle / Listening — big mic */}
          {(phase === 'idle' || phase === 'listening') && (
            <>
              <div style={{ position: 'relative', width: 100, height: 100, margin: '8px 0 16px' }}>
                {/* Gradient ring */}
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  background: `conic-gradient(from 0deg, ${C.green}, ${C.accent}, ${C.gold}, ${C.purple}, ${C.green})`,
                  animation: phase === 'listening' ? 'ring-spin 2.5s linear infinite' : 'none',
                }} />
                <div style={{ position: 'absolute', inset: 3, borderRadius: '50%', background: C.bg }} />
                {/* Glow */}
                {phase === 'listening' && (
                  <div style={{
                    position: 'absolute', inset: -8, borderRadius: '50%',
                    background: `conic-gradient(from 90deg, ${C.green}30, ${C.accent}30, ${C.gold}30, ${C.purple}30, ${C.green}30)`,
                    filter: 'blur(16px)', animation: 'glow-breathe 2s ease-in-out infinite',
                  }} />
                )}
                {/* Button */}
                <button
                  data-testid="voice-popup-mic"
                  onClick={phase === 'listening' ? stopListening : startListening}
                  style={{
                    position: 'relative', zIndex: 2, width: 76, height: 76, borderRadius: '50%',
                    border: 'none', cursor: 'pointer',
                    background: phase === 'listening'
                      ? `linear-gradient(135deg, ${C.accent}, ${C.gold})`
                      : `linear-gradient(135deg, ${C.green}, ${C.accent} 50%, ${C.purple})`,
                    boxShadow: phase === 'listening' ? `0 4px 24px ${C.accent}44` : `0 4px 16px rgba(90,140,106,0.2)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '12px auto 0', transition: 'all 0.3s ease',
                  }}
                >
                  {phase === 'listening'
                    ? <MicOff style={{ width: 28, height: 28, color: '#FFF' }} />
                    : <Mic style={{ width: 28, height: 28, color: '#FFF' }} />
                  }
                </button>
              </div>
              <p style={{ fontSize: 13, color: C.muted, fontFamily: 'var(--font-sans)', textAlign: 'center', margin: '0 0 12px' }}>
                {phase === 'listening' ? 'Tap to stop' : 'Tap to start listening'}
              </p>
              {/* Text fallback removed — this is a voice-only feature */}
            </>
          )}

          {/* Processing */}
          {phase === 'processing' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }}>
              <div style={{ position: 'relative', width: 80, height: 80, margin: '0 0 16px' }}>
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  background: `conic-gradient(from 0deg, ${C.green}, ${C.accent}, ${C.gold}, ${C.purple}, ${C.green})`,
                  animation: 'ring-spin 2s linear infinite', opacity: 0.3,
                }} />
                <div style={{ position: 'absolute', inset: 3, borderRadius: '50%', background: C.bg }} />
                <Loader2 style={{ position: 'relative', zIndex: 2, width: 28, height: 28, color: C.accent, animation: 'spin 1s linear infinite', margin: '26px auto 0' }} />
              </div>
              {transcript && (
                <p style={{ fontSize: 13, color: C.textSec, fontFamily: 'var(--font-sans)', textAlign: 'center', fontStyle: 'italic', maxWidth: 300 }}>
                  "{transcript}"
                </p>
              )}
            </div>
          )}

          {/* Result */}
          {phase === 'result' && result && (
            <div style={{ width: '100%' }}>
              {/* Success indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: `${C.green}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Check style={{ width: 18, height: 18, color: C.green }} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.text, fontFamily: 'var(--font-sans)' }}>
                    {result.logged_meal_id ? 'Meal logged' : 'Noted'}
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: C.muted, fontFamily: 'var(--font-sans)' }}>
                    "{transcript}"
                  </p>
                </div>
              </div>

              {/* Totals */}
              {result.today_totals && (
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
                  padding: 14, borderRadius: 16, background: '#FFF', border: '1px solid #E8E3DB',
                  marginBottom: 12,
                }}>
                  {[
                    { label: 'Cal', val: Math.round(result.today_totals.calories || 0), color: C.text },
                    { label: 'Protein', val: `${Math.round(result.today_totals.protein || 0)}g`, color: C.green },
                    { label: 'Carbs', val: `${Math.round(result.today_totals.carbs || 0)}g`, color: C.gold },
                    { label: 'Fat', val: `${Math.round(result.today_totals.fat || 0)}g`, color: C.accent },
                  ].map(m => (
                    <div key={m.label} style={{ textAlign: 'center' }}>
                      <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: m.color, fontFamily: 'var(--font-sans)' }}>{m.val}</p>
                      <p style={{ margin: 0, fontSize: 10, color: C.muted, fontFamily: 'var(--font-sans)' }}>{m.label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Nuri response */}
              {result.ai_message?.content && (
                <p style={{ fontSize: 13, color: C.textSec, fontFamily: 'var(--font-sans)', lineHeight: 1.5, margin: '0 0 16px' }}>
                  {result.ai_message.content.length > 200 ? result.ai_message.content.substring(0, 200) + '...' : result.ai_message.content}
                </p>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button data-testid="voice-popup-another" onClick={handleReset} style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px 16px', borderRadius: 20, border: `1px solid #E8E3DB`,
                  background: '#FFF', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                  color: C.text, fontFamily: 'var(--font-sans)',
                }}>
                  <RotateCcw style={{ width: 14, height: 14 }} /> Log another
                </button>
                <button data-testid="voice-popup-done" onClick={onClose} style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px 16px', borderRadius: 20, border: 'none',
                  background: `linear-gradient(135deg, ${C.green}, ${C.accent})`,
                  cursor: 'pointer', fontSize: 13, fontWeight: 500,
                  color: '#FFF', fontFamily: 'var(--font-sans)',
                }}>
                  <Check style={{ width: 14, height: 14 }} /> Done
                </button>
              </div>
            </div>
          )}

          {/* Error */}
          {phase === 'error' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ fontSize: 14, color: C.accent, fontFamily: 'var(--font-sans)', margin: '0 0 12px' }}>Something went wrong</p>
              <button onClick={handleReset} style={{
                padding: '10px 24px', borderRadius: 20, border: 'none', cursor: 'pointer',
                background: C.accent, color: '#FFF', fontSize: 13, fontWeight: 500,
                fontFamily: 'var(--font-sans)',
              }}>Try again</button>
            </div>
          )}
        </div>

        <style>{`
          @keyframes voice-popup-slide {
            from { transform: translateY(100%); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes ring-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes glow-breathe {
            0%, 100% { opacity: 0.5; transform: scale(1); }
            50% { opacity: 0.85; transform: scale(1.08); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
